/**
 * Card invitations + workspace members API surface.
 *
 * Backed by /api/kanban/cards/:cardId/invitations and
 * /api/workspaces/:id/members on the gateway.
 */
import type {
  CardInvitationWithUser,
  InviteToCardInput,
  WorkspaceMemberSummary,
} from '@chaos/types/project';

import { apiJson } from './client';

interface InvitationsResponse {
  invitations: CardInvitationWithUser[];
}
interface InvitationResponse {
  invitation: CardInvitationWithUser;
}
interface WorkspaceMembersResponse {
  members: WorkspaceMemberSummary[];
}

export const invitationsApi = {
  list(cardId: string): Promise<CardInvitationWithUser[]> {
    return apiJson<InvitationsResponse>(`/kanban/cards/${cardId}/invitations`).then(
      (r) => r.invitations,
    );
  },
  invite(cardId: string, input: InviteToCardInput): Promise<CardInvitationWithUser> {
    return apiJson<InvitationResponse>(`/kanban/cards/${cardId}/invitations`, {
      method: 'POST',
      json: input,
    }).then((r) => r.invitation);
  },
  revoke(cardId: string, userId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/kanban/cards/${cardId}/invitations/${userId}`, {
      method: 'DELETE',
    });
  },
};

export const invitationKeys = {
  all: ['invitations'] as const,
  list: (cardId: string) => [...invitationKeys.all, cardId] as const,
};

export const workspaceMembersApi = {
  list(workspaceId: string): Promise<WorkspaceMemberSummary[]> {
    return apiJson<WorkspaceMembersResponse>(`/workspaces/${workspaceId}/members`).then(
      (r) => r.members,
    );
  },
};

export const workspaceMemberKeys = {
  all: ['workspace-members'] as const,
  list: (workspaceId: string) => [...workspaceMemberKeys.all, workspaceId] as const,
};
